import type { LossOutcome, StockPiece, StockPolicy, VerificationComplete, VerificationResult } from '@miraki/contracts';
import { ApiError } from '../api/client';

/**
 * A-43's stock half — CN-09 recall, CN-10 losses, CN-11 counts — what the screen decides, kept pure
 * and tested. The rules are the server's (`stock.rules.ts`); this decides what to OFFER and how to
 * say what was refused.
 */

type Line = { key: string; values: Record<string, string> };

/**
 * The portal's answers to D20, D39/D81 and D40 as sentences, so the person acting can see which
 * rule they are acting under — and that an open decision is open, rather than an empty setting.
 */
export function policyLines(p: StockPolicy): Line[] {
  const r = p.recallTrigger;
  return [
    r.type === 'NONE' ? { key: 'stock.policy.recallNONE', values: {} } : { key: `stock.policy.recall${r.type}`, values: { days: String(r.afterDays) } },
    p.lossDefaultOutcome === null ? { key: 'stock.policy.lossNone', values: {} }
      : { key: 'stock.policy.lossDefault', values: { outcome: p.lossDefaultOutcome } },
    p.verificationCycleDays === null ? { key: 'stock.policy.cycleNone', values: {} }
      : { key: 'stock.policy.cycle', values: { days: String(p.verificationCycleDays) } },
  ];
}

/**
 * D39/D81 — the outcome to show first. The portal's default if one is set AND may be recorded from
 * here; otherwise nothing, so the person must choose. The server takes no default either way.
 */
export function firstOutcome(next: readonly LossOutcome[], setting: StockPolicy['lossDefaultOutcome']): LossOutcome | '' {
  return setting !== null && next.includes(setting) ? setting : '';
}

/** How a held piece's live request reads on the stock list: a recall is said as one. */
export function returnLabel(r: StockPiece['returnRequest']): 'REQUESTED' | 'APPROVED' | 'RECALL' | null {
  if (!r) return null;
  if (r.origin === 'MIRAKI') return 'RECALL';
  return r.state === 'REQUESTED' || r.state === 'APPROVED' ? r.state : null;
}

/** CN-10 — the server refuses a piece on a live request; the screen does not offer it. */
export const reportable = (p: Pick<StockPiece, 'returnRequest'>) => p.returnRequest === null;
/** CN-09 — a piece already on a live request is not recalled again. */
export const recallable = (p: Pick<StockPiece, 'returnRequest'>) => p.returnRequest === null;

export type CountDraft = Readonly<Record<string, { result?: VerificationResult; note?: string }>>;

/**
 * CN-11 — the count to send, or the pieces still without a result. Every listed piece needs one;
 * a blank is held back here, by piece, rather than relayed as a 409.
 */
export function countPayload(
  lines: ReadonlyArray<{ pieceId: string; description: string }>, draft: CountDraft, countedOn: string, remarks: string,
): { ok: true; payload: VerificationComplete } | { ok: false; missing: string[]; noDate: boolean } {
  const missing = lines.filter((l) => !draft[l.pieceId]?.result).map((l) => l.description);
  if (missing.length > 0 || countedOn === '') return { ok: false, missing, noDate: countedOn === '' };
  return {
    ok: true,
    payload: {
      countedOn,
      results: lines.map((l) => {
        const d = draft[l.pieceId]!;
        const note = d.note?.trim();
        return { pieceId: l.pieceId, result: d.result!, ...(note ? { note } : {}) };
      }),
      ...(remarks.trim() ? { remarks: remarks.trim() } : {}),
    },
  };
}

/** The refusal codes this half's routes answer with, and where each keeps its list. */
const LISTS = new Set(['LOSS_REPORT_REFUSED', 'RECALL_REFUSED', 'COUNT_REFUSED']);
const SINGLE = new Set(['COUNT_OPEN', 'NOTHING_HELD']);

/**
 * A 409 from any stock route, as one line per refusal naming the piece by Job Number. Null when the
 * error is something else, so it is shown as the error it is rather than dressed as a refusal.
 */
export function stockRefusalLines(err: unknown, jobOf: (pieceId: string) => string | undefined): Line[] | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const d = err.detail as { code?: string; refusals?: unknown; refusal?: unknown } | undefined;
  const code = d?.code ?? '';
  const one = (r: Record<string, unknown>): Line => {
    const s = (k: string) => (typeof r[k] === 'string' ? (r[k]) : '');
    const id = s('pieceId');
    return {
      key: `stock.refusal.${s('code')}`,
      values: {
        job: (id && jobOf(id)) || id, status: s('status'), state: s('state'),
        countedOn: s('countedOn'), listedOn: s('listedOn'),
      },
    };
  };
  if (LISTS.has(code) && Array.isArray(d?.refusals)) return (d.refusals as Array<Record<string, unknown>>).map(one);
  if (code === 'LOSS_OUTCOME_REFUSED' && d?.refusal && typeof d.refusal === 'object') return [one(d.refusal as Record<string, unknown>)];
  if (SINGLE.has(code)) return [{ key: `stock.refusal.${code}`, values: {} }];
  return null;
}
